import { defineStore } from "pinia";
import { ref } from "vue";
import service from "@/services/crm/deal.service";
import * as Toast from "@/utils/toast";
import { createScopedCache } from "@/stores/_cache";

const dealListCache = createScopedCache("deals:list");
const dealDetailCache = createScopedCache("deals:detail");

const DEAL_LIST_TTL = 2 * 60 * 1000;
const DEAL_DETAIL_TTL = 5 * 60 * 1000;

const notifyError = (msg) => {
  if (Toast?.toast?.error) return Toast.toast.error(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "error");
  if (Toast?.error) return Toast.error(msg);
  if (typeof Toast === "function") return Toast(msg, "error");
  console.error(msg);
};

const notifySuccess = (msg) => {
  if (Toast?.toast?.success) return Toast.toast.success(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "success");
  if (Toast?.success) return Toast.success(msg);
  if (typeof Toast === "function") return Toast(msg, "success");
  console.log(msg);
};

export const useDealStore = defineStore("deal", () => {
  const items = ref([]);
  const byId = ref(Object.create(null));
  const pagination = ref({ total: 0, per_page: 25, current_page: 1 });
  const loading = ref(false);
  const error = ref(null);

  const filters = ref({
    q: "",
    status: "",
    pipeline_id: null,
    stage_id: null,
    owner_id: null,
    date_from: "",
    date_to: "",
    per_page: 25,
    page: 1,
  });

  function normalize(raw = {}) {
    return {
      id: raw.id,
      name: raw.name || "-",
      title: raw.title || raw.name || "-",
      status: raw.status || "Open",
      client_id: raw.client_id ?? raw.client?.id ?? null,
      pipeline_id: raw.pipeline_id ?? raw.pipeline?.id ?? null,
      stage_id: raw.stage_id ?? raw.stage?.id ?? null,
      owner_id: raw.owner_id ?? raw.owner?.id ?? null,
      amount: Number(raw.amount ?? 0),
      currency: raw.currency || "KES",
      probability: raw.probability,
      expected_close_date: raw.expected_close_date || null,
      next_follow_up: raw.next_follow_up || null,
      priority: raw.priority || null,
      company_name: raw.company_name || "",
      client: raw.client || null,
      contact_name: raw.contact_name || "",
      contact_email: raw.contact_email || "",
      contact_phone: raw.contact_phone || "",
      source: raw.source || "",
      tags: Array.isArray(raw.tags) ? raw.tags : [],
      description: raw.description || "",
      visibility: raw.visibility || "public",
      user_ids: Array.isArray(raw.user_ids) ? raw.user_ids : [],
      pipeline: raw.pipeline || null,
      stage: raw.stage || null,
      owner: raw.owner || null,
      created_at: raw.created_at || null,
      updated_at: raw.updated_at || null,
    };
  }

  function replaceList(rows, meta) {
    items.value = rows.map(normalize);
    byId.value = Object.fromEntries(items.value.map((x) => [x.id, x]));
    pagination.value = meta;
  }

  function upsertOne(raw) {
    const n = normalize(raw);
    byId.value[n.id] = n;
    const idx = items.value.findIndex((x) => x.id === n.id);
    if (idx === -1) items.value.unshift(n);
    else items.value.splice(idx, 1, n);
    return n;
  }

  async function list(params = {}, options = {}) {
    return fetchAll(params, options);
  }

  async function fetchAll(extra = {}, options = {}) {
    loading.value = true;
    error.value = null;
    try {
      const params = { ...filters.value, ...extra };
      return await dealListCache.get(params, {
        ttlMs: DEAL_LIST_TTL,
        force: options.force === true,
        loader: async () => {
          const { data } = await service.list(params);
          if (data && data.data && data.meta) {
            return {
              rows: Array.isArray(data.data) ? data.data : [],
              meta: {
                total: data.meta.total,
                per_page: data.meta.per_page,
                current_page: data.meta.current_page,
              },
            };
          }

          const listRows = data?.data ?? data ?? [];
          return {
            rows: Array.isArray(listRows) ? listRows : [],
            meta: {
              total: data?.total ?? (Array.isArray(listRows) ? listRows.length : 0),
              per_page: data?.per_page ?? params.per_page ?? 25,
              current_page: data?.current_page ?? params.page ?? 1,
            },
          };
        },
        onHydrate: (payload) => {
          replaceList(payload.rows || [], payload.meta || pagination.value);
        },
      }).then(() => ({
        data: {
          data: items.value,
          meta: pagination.value,
        },
      }));
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to fetch deals";
      notifyError(error.value);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function ensureList(extra = {}, options = {}) {
    return fetchAll(extra, options);
  }

  async function fetchOne(id, options = {}) {
    if (!id) return null;
    loading.value = true;
    error.value = null;
    try {
      await dealDetailCache.get(
        { id },
        {
          ttlMs: DEAL_DETAIL_TTL,
          force: options.force === true,
          loader: async () => {
            const { data } = await service.get(id);
            return { row: data };
          },
          onHydrate: (payload) => {
            if (payload?.row) upsertOne(payload.row);
          },
        },
      );
      return byId.value[id] || null;
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to fetch deal";
      notifyError(error.value);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function ensureOne(id, options = {}) {
    return fetchOne(id, options);
  }

  function invalidateDealCaches() {
    dealListCache.invalidate();
    dealDetailCache.invalidate();
  }

  async function create(payload) {
    loading.value = true;
    error.value = null;
    try {
      const { data } = await service.create(payload);
      invalidateDealCaches();
      upsertOne(data);
      notifySuccess("Deal created");
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to create deal";
      error.value = msg;
      notifyError(msg);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function update(id, payload) {
    loading.value = true;
    error.value = null;
    try {
      const { data } = await service.update(id, payload);
      dealListCache.invalidate();
      dealDetailCache.invalidate({ id });
      upsertOne(data);
      notifySuccess("Deal updated");
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to update deal";
      error.value = msg;
      notifyError(msg);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function remove(id) {
    loading.value = true;
    error.value = null;
    try {
      await service.remove(id);
      invalidateDealCaches();
      delete byId.value[id];
      const idx = items.value.findIndex((x) => x.id === id);
      if (idx !== -1) items.value.splice(idx, 1);
      notifySuccess("Deal deleted");
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to delete deal";
      error.value = msg;
      notifyError(msg);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function get(id, options = {}) {
    return fetchOne(id, options);
  }

  async function show(id, options = {}) {
    return fetchOne(id, options);
  }

  async function find(id, options = {}) {
    return fetchOne(id, options);
  }

  return {
    items,
    list,
    byId,
    pagination,
    loading,
    error,
    filters,
    fetchAll,
    ensureList,
    fetchOne,
    ensureOne,
    get,
    show,
    find,
    create,
    update,
    remove,
    invalidateDealCaches,
  };
});
